import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, BrandLogo, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, FormField, Input, PasswordInput } from '@payment-plan/ui';
import { loginSchema, type LoginFormValues } from './login.schema';
import { useAuth } from '../../auth/auth-context';
import { ENLACE_CLASS } from '../auth/AuthCard';

export function LoginPage(): React.JSX.Element {
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { correo: '', contrasena: '' },
  });

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // fix-60: aviso al volver de definir la contraseña (enlace de invitación o recuperación).
  const aviso = (location.state as { aviso?: string } | null)?.aviso;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      await login(values.correo, values.contrasena);
      const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (cause) {
      setServerError(cause instanceof Error ? cause.message : 'No fue posible iniciar sesión.');
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <BrandLogo variant="completo" size="lg" className="mb-4 self-start" />
          <CardTitle>Iniciar sesión</CardTitle>
          <CardDescription>Accede a tu espacio de trabajo.</CardDescription>
        </CardHeader>
        <CardContent>
          {aviso && (
            <Alert variant="success" role="status" className="mb-5">
              {aviso}
            </Alert>
          )}
          <form className="space-y-5" onSubmit={(event) => void onSubmit(event)} noValidate>
            <FormField label="Correo" htmlFor="correo" required error={errors.correo?.message}>
              <Input id="correo" type="email" autoComplete="email" autoFocus error={!!errors.correo} {...register('correo')} />
            </FormField>
            <FormField label="Contraseña" htmlFor="contrasena" required error={errors.contrasena?.message}>
              <PasswordInput id="contrasena" autoComplete="current-password" {...register('contrasena')} error={!!errors.contrasena} aria-invalid={!!errors.contrasena} />
            </FormField>
            {serverError && <p role="alert" className="text-sm text-destructive">{serverError}</p>}
            <Button className="w-full" type="submit" disabled={isSubmitting} loading={isSubmitting}>
              {isSubmitting ? 'Ingresando...' : 'Ingresar'}
            </Button>
            <p className="text-center">
              <Link to="/recuperar" className={ENLACE_CLASS}>
                ¿Olvidaste tu contraseña?
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}